export interface PhoneValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export function validatePhoneNumber(
  phoneNumber: string,
  countryCode?: string
): PhoneValidationResult {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return {
      isValid: false,
      errorMessage: 'Phone number is required',
    };
  }

  // Allow digits, spaces, dashes, dots, and plus sign
  const ALLOWED_CHARS = ['+', '-', '.', ' '];

  // Check if phone number contains only allowed characters
  const hasOnlyAllowedChars = phoneNumber
    .split('')
    .every((char) => char.match(/\d/) || ALLOWED_CHARS.includes(char));

  if (!hasOnlyAllowedChars) {
    return {
      isValid: false,
      errorMessage:
        'Phone number can only contain digits, spaces, and the following characters: + - .',
    };
  }

  // Get only digits and plus signs (like backend digits_and_plus)
  const digitsAndPlus = phoneNumber.replace(/[^\d+]/g, '');

  // If there's +, it must be the first character and there must be only one
  if (digitsAndPlus.includes('+')) {
    // Is there only one?
    if ((digitsAndPlus.match(/\+/g) || []).length !== 1) {
      return {
        isValid: false,
        errorMessage: "Phone number must not contain more than one '+'",
      };
    }
    if (digitsAndPlus.indexOf('+') !== 0) {
      return {
        isValid: false,
        errorMessage: "'+' must be at the start of the phone number.",
      };
    }
  }

  const MIN = 10;
  const MAX = 15;

  // Get only digits for length validation (like backend digits_only)
  const digitsOnly = digitsAndPlus.replace(/\D/g, '');

  // Handle USA and Canada with specific validation
  if (countryCode === 'US' || countryCode === 'CA') {
    // Phone number must have exactly 10 digits with an optional 1 or +1 prefix
    let isValid = false;

    if (digitsAndPlus.startsWith('1')) {
      // First 1 can be country or actual number in the phone number
      // in this case we accept both length 10 and 11
      // 1 123.456.789.0   -> 11 digits with 1 as prefix
      // 123.456.789.0     -> 10 digits, no prefix
      if (digitsAndPlus.length >= 10 && digitsAndPlus.length <= 11) {
        isValid = true;
      }
    } else if (digitsAndPlus.startsWith('+1')) {
      // We expect number to be exactly 12 digits when there's +1
      // +1 123.456.789.0
      if (digitsAndPlus.length === 12) {
        isValid = true;
      }
    } else if (digitsAndPlus.length === 10) {
      isValid = true;
    }

    if (!isValid) {
      return {
        isValid: false,
        errorMessage: `Phone number for ${
          countryCode === 'US' ? 'USA' : 'Canada'
        } must have exactly 10 digits, or 10 digits preceded by '1' or '+1'`,
      };
    }
  } else {
    // Handle all other countries
    // eslint-disable-next-line no-lonely-if
    if (!(MIN <= digitsOnly.length && digitsOnly.length <= MAX)) {
      return {
        isValid: false,
        errorMessage: 'Phone number must be between 10 and 15 characters',
      };
    }
  }

  return { isValid: true };
}

// Taken from checkout-app validateEmail function - https://github.com/xometry/checkout-app/blob/main/src/helpers.js#L77
const EMAIL_REGEX =
  /^[a-zA-Z0-9.%&’+/=?^_`'{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,})+$/;

/**
 * Validates a given string is a valid email address
 *
 * @param value The string to be validated
 * @returns boolean
 */
export function validateEmail(value: string) {
  return EMAIL_REGEX.test(value);
}
